import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import {
  APPLICATION_MODULES,
  APPLICATION_REQUEST_STATUSES,
  labelFor,
  toneFor,
} from "const/referralEnums";
import { Panel, StatusChip, EmptyState, chipTone } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerClients } from "../../api/endpoints/workspace";
import {
  createPartnerApplicationRequest,
  fetchPartnerApplicationRequests,
  fetchPartnerApplications,
} from "../../api/endpoints/applications";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { formatDate } from "../../utils/portalHelpers";
import { RequestDialog } from "../Clients/ClientApplicationsTab";
import ClientPicker from "../Documents/ClientPicker";
import { RECENT_APPLICATION_CLIENTS, recentApplications } from "./dashboardHelpers";

const RECENT_CLIENTS_PARAMS = { page: 0, size: RECENT_APPLICATION_CLIENTS };
const ALL_REQUESTS_PARAMS = {};

/** The new-application form, with the client chosen first instead of coming from a client page. */
const NewApplicationDialog = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [client, setClient] = useState(null);

  const close = () => {
    setClient(null);
    onClose();
  };

  const create = useMutation((body) => createPartnerApplicationRequest(client.clientId, body), {
    onSuccess: () => {
      toast.success(`Sent to the office for ${client?.name || "your client"}`);
      queryClient.invalidateQueries(partnerQueryKeys.applicationRequests(ALL_REQUESTS_PARAMS));
      queryClient.invalidateQueries(
        partnerQueryKeys.applicationRequests({ clientId: client?.clientId })
      );
      queryClient.invalidateQueries(partnerQueryKeys.applications(client?.clientId));
      close();
    },
  });

  return (
    <RequestDialog
      open={open}
      initial={null}
      clientName={client?.name}
      clientMissing={!client}
      clientSlot={<ClientPicker value={client} onChange={setClient} disabled={create.isLoading} />}
      onClose={close}
      onSaved={(body) => create.mutate(body)}
      saving={create.isLoading}
      error={create.isError}
    />
  );
};

/**
 * The latest applications across the partner's recent clients, with their status, and the way to
 * start a new one without first opening a client.
 */
const RecentApplicationsPanel = () => {
  const [creating, setCreating] = useState(false);

  const { data: clientsPage, isLoading: clientsLoading } = usePartnerQuery(
    partnerQueryKeys.clients(RECENT_CLIENTS_PARAMS),
    () => fetchPartnerClients(RECENT_CLIENTS_PARAMS)
  );
  const clients = useMemo(() => clientsPage?.content || [], [clientsPage]);

  // Same keys as each client's Applications tab, so opening a client reuses what is loaded here.
  const applicationQueries = useQueries({
    queries: clients.map((client) => ({
      queryKey: partnerQueryKeys.applications(client.clientId),
      queryFn: () => fetchPartnerApplications(client.clientId),
      refetchOnMount: true,
    })),
  });

  const { data: requests } = usePartnerQuery(
    partnerQueryKeys.applicationRequests(ALL_REQUESTS_PARAMS),
    () => fetchPartnerApplicationRequests(ALL_REQUESTS_PARAMS)
  );

  const applicationData = applicationQueries.map((query) => query.data);
  const rows = useMemo(
    () =>
      recentApplications({
        clients,
        applicationsByClient: Object.fromEntries(
          clients.map((client, index) => [
            client.clientId,
            Array.isArray(applicationData[index]) ? applicationData[index] : [],
          ])
        ),
        requests: Array.isArray(requests) ? requests : [],
      }),
    // applicationData is rebuilt each render; its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients, requests, JSON.stringify(applicationData)]
  );
  const loading = clientsLoading || applicationQueries.some((query) => query.isLoading);

  return (
    <Panel
      title="Recent applications"
      action={
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          sx={{ bgcolor: "#0f766e", "&:hover": { bgcolor: "#115e59" } }}
        >
          New application
        </Button>
      }
    >
      {loading && !rows.length ? (
        <p className="m-0 py-4 text-sm text-slate-500">Loading applications…</p>
      ) : rows.length ? (
        <ul className="m-0 list-none p-0">
          {rows.map((row) => {
            const isRequest = row.kind === "request";
            const body = (
              <>
                <div className="min-w-0">
                  <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-teal-700">
                    {labelFor(APPLICATION_MODULES, row.module)}
                    {row.clientName ? (
                      <span className="font-medium normal-case tracking-normal text-slate-500">
                        {" "}
                        · {row.clientName}
                      </span>
                    ) : null}
                  </p>
                  <p className="m-0 mt-0.5 truncate text-sm font-medium text-slate-900">
                    {row.title || (isRequest ? "New application request" : "Application")}
                  </p>
                  <p className="m-0 mt-0.5 truncate text-xs text-slate-500">
                    {row.subtitle ? `${row.subtitle} · ` : ""}
                    {isRequest ? "Requested" : "Updated"} {formatDate(row.at)}
                  </p>
                </div>
                {isRequest ? (
                  <StatusChip tone={chipTone(toneFor(APPLICATION_REQUEST_STATUSES, row.status))}>
                    {labelFor(APPLICATION_REQUEST_STATUSES, row.status)}
                  </StatusChip>
                ) : (
                  <StatusChip tone="neutral">{row.status}</StatusChip>
                )}
              </>
            );
            const className =
              "flex items-start justify-between gap-3 border-b border-slate-100 py-2.5 last:border-0";
            return (
              <li key={row.key}>
                {row.clientId ? (
                  <Link
                    to={`/partner/clients/${row.clientId}`}
                    className={`${className} no-underline hover:bg-slate-50 -mx-2 px-2 rounded-lg`}
                  >
                    {body}
                  </Link>
                ) : (
                  <div className={className}>{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          title="No applications yet"
          description="Start one for any of your clients, and the office takes it from there."
        />
      )}

      <NewApplicationDialog open={creating} onClose={() => setCreating(false)} />
    </Panel>
  );
};

export default RecentApplicationsPanel;
